import _setTimeout from "@babel/runtime-corejs3/core-js/set-timeout";
import React from "react";
import PropTypes from "prop-types";
import { useUniqueID } from "@xo-union/react-unique-id";
import noop from "lodash/noop";
import scrollIntoView from "scroll-into-view-if-needed";
import { OptionContext } from "../OptionContext/index.js";
import { useSelectContext } from "../SelectContext/index.js";
import useErrorOnChange from "../useErrorOnChange.js";

/**
 * This component has to wrap all Option components.
 *
 * Integrates with parent `SelectContainer` to:
 *
 * 1. Sync selection state of option
 * 2. Create an abstraction that facilitates introspecting current option state.
 *    This facilitates answering the questions: "Is the option selected?", "Is the option active (keyboard selected)?"
 *
 * #### Option abstraction
 *
 * The option abstraction is accessible via a render prop or via context
 *
 * For example:
 *
 * ```jsx
 * import { OptionContainer } from '@xo-union/ui-select';
 *
 * <OptionContainer>
 *   {(option) => {
 *      // use `option` abstraction
 *   }}
 * </OptionContainer>
 * ```
 *
 * Via context (`MyComponent` must be nested inside of `OptionContainer`)
 *
 * ```jsx
 * import { useOption } from '@xo-union/ui-select';
 *
 * const MyComponent = () => {
 *   const option = useOption();
 *   // use `option` abstraction
 * }
 * ```
 *
 * #### Option abstraction properties
 *
 * | Property | Type | Description |
 * |----------|------|-------------|
 * | `option.isSelected` | `bool` | True if the `SelectContainer`'s `value` matches the `OptionContainer`'s value |
 * | `option.isActive` | `bool` | True if the option is selected with keyboard |
 * | `option.isFocused` | `bool` | When `SelectContainer` is open, an `active` option is also focused. Otherwise, a `selected` option is focused. |
 * | `option.optionElementRef` | `React.RefObject<HTMLElement>` | Ref to underlying HTML element |
 * | `option.selectOption` | `(eventObject?): void` |  Trigger selection of this option. |
 * | `option.value` | `string` |  Option's value |
 * | `option.id` | `string` |  Option's id |
 *
 */
const OptionContainer = _ref => {
  var _select$selectedOptio, _select$activeOption;
  let {
    id: idProp,
    value,
    children,
    onSelect
  } = _ref;
  useErrorOnChange([idProp, value], '`id` and `value` props cannot change on OptionContainer. Please use static value');
  const id = useUniqueID(idProp);
  const select = useSelectContext();
  const optionElementRef = React.useRef();
  const isSelected = ((_select$selectedOptio = select.selectedOption) === null || _select$selectedOptio === void 0 ? void 0 : _select$selectedOptio.id) === id;
  const isActive = ((_select$activeOption = select.activeOption) === null || _select$activeOption === void 0 ? void 0 : _select$activeOption.id) === id;
  const isFocused = select.isOpen ? isActive : isSelected;
  const changeEvent = React.useMemo(() => {
    const evt = {
      option: {
        value,
        id
      },
      // Allow `e.target.value` pattern to make custom select interoperable
      // with native selects
      target: {
        value
      }
    };
    return evt;
  }, [value, id]);
  const {
    onChange
  } = select;
  const selectOption = React.useCallback(originalEvent => {
    onSelect(changeEvent, originalEvent);
    onChange(changeEvent, originalEvent);
  }, [onSelect, onChange, changeEvent]);
  React.useEffect(() => {
    const optionForParentSelect = {
      ref: optionElementRef,
      select: selectOption,
      value,
      id
    };
    select.addOption(optionForParentSelect);
    return () => {
      select.removeOption(id);
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  React.useEffect(() => {
    // Patch to force SSR to work with hydrate
    // Since different IDs are being generated on the server and client side
    // And react will not ensure properties that mismatch are updated.
    if (optionElementRef.current && optionElementRef.current.id !== id) {
      optionElementRef.current.id = id;
    }
  }, []);
  React.useEffect(() => {
    _setTimeout(() => {
      if (isActive && optionElementRef.current && select.isOpen) {
        scrollIntoView(optionElementRef.current, {
          behavior: 'auto',
          scrollMode: 'if-needed'
        });
      }
    });
  }, [isActive, select.isOpen]);
  const optionContext = React.useMemo(() => ({
    isSelected,
    isActive,
    isFocused,
    optionElementRef,
    selectOption,
    id,
    value
  }), [id, value, isSelected, isActive, isFocused, selectOption]);
  return /*#__PURE__*/React.createElement(OptionContext.Provider, {
    value: optionContext
  }, typeof children === 'function' ? children(optionContext) : children);
};
process.env.NODE_ENV !== "production" ? OptionContainer.propTypes = {
  /**
   * Override option id. This prop has to be a static string
   */
  id: PropTypes.string,
  /**
   * Set option value. This prop has to be a static string
   */
  value: PropTypes.string.isRequired,
  /**
   * A render function that receives the `option` object or the Option's children
   */
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  /**
   * Called when option is selected
   */
  onSelect: PropTypes.func
} : void 0;
OptionContainer.defaultProps = {
  onSelect: noop
};
export default OptionContainer;